import { isSupabaseConfigured, supabase } from "../lib/supabase";

const LOCAL_STORAGE_KEY = "almoune_coupons";
const COUPON_discount = 3.00;
const COUPON_minSpend = 30.00;
const COUPON_validityWeeks = 3;

function generateCouponCode() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let code = "ALM-";
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

export const getCoupons = () => {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

export const saveCouponsLocal = (coupons) => {
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(coupons));
};

export const generateCoupon = async ({ orderId, total, cashierId, cashierName }) => {
  const now = new Date();
  const expiresAt = new Date(now);
  expiresAt.setDate(expiresAt.getDate() + COUPON_validityWeeks * 7);

  const coupon = {
    id: `coup-${Date.now()}`,
    code: generateCouponCode(),
    discount: COUPON_discount,
    minSpend: COUPON_minSpend,
    orderId,
    total,
    cashier_id: cashierId || "unknown",
    cashier_name: cashierName || "Caissier",
    created_at: now.toISOString(),
    expires_at: expiresAt.toISOString(),
    used: false,
    used_at: null,
    used_order_id: null,
  };

  const local = getCoupons();
  const updated = [coupon, ...local];
  saveCouponsLocal(updated);

  if (isSupabaseConfigured && supabase) {
    try {
      const { error } = await supabase.from("coupons").insert({
        id: coupon.id,
        code: coupon.code,
        discount: coupon.discount,
        min_spend: coupon.min_spend,
        order_id: coupon.order_id,
        total: coupon.total,
        cashier_id: coupon.cashier_id,
        cashier_name: coupon.cashier_name,
        created_at: coupon.created_at,
        expires_at: coupon.expires_at,
        used: false,
      });
      if (!error) {
        coupon.synced = true;
        saveCouponsLocal(updated.map((c) => (c.id === coupon.id ? coupon : c)));
      }
    } catch (err) {
      console.error("Failed to sync coupon to Supabase:", err);
    }
  }

  return coupon;
};

export const validateCoupon = (code) => {
  const coupons = getCoupons();
  const coupon = coupons.find(
    (c) => c.code.toUpperCase() === code.toUpperCase() && !c.used
  );
  if (!coupon) return { valid: false, error: "Coupon introuvable ou déjà utilisé" };

  const now = new Date();
  if (new Date(coupon.expires_at) < now) {
    return { valid: false, error: "Coupon expiré" };
  }

  return { valid: true, coupon };
};

export const markCouponUsed = async (couponId, usedOrderId) => {
  const local = getCoupons();
  const updated = local.map((c) =>
    c.id === couponId
      ? { ...c, used: true, used_at: new Date().toISOString(), used_order_id: usedOrderId }
      : c
  );
  saveCouponsLocal(updated);

  if (isSupabaseConfigured && supabase) {
    try {
      await supabase
        .from("coupons")
        .update({ used: true, used_at: new Date().toISOString(), used_order_id: usedOrderId })
        .eq("id", couponId);
    } catch (err) {
      console.error("Failed to mark coupon as used in Supabase:", err);
    }
  }
};

export const isEligibleForCoupon = (total) => total >= 50;
export const COUPON_CONFIG = { discount: COUPON_discount, minSpend: COUPON_minSpend, validityWeeks: COUPON_validityWeeks };

// --- Promo coupon management (used by MyAccountAddress admin page) ---
export const couponTypeOptions = [
  { value: "percent", label: "Pourcentage (%)" },
  { value: "fixed", label: "Montant fixe (€)" },
];

export const loadCoupons = async () => {
  const local = getCoupons();
  if (!isSupabaseConfigured || !supabase) return local;
  try {
    const { data, error } = await supabase
      .from("coupons")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) return local;
    return data || local;
  } catch {
    return local;
  }
};

export const saveCoupon = async (couponData) => {
  const local = getCoupons();
  const coupon = {
    id: couponData.id || `coup-${Date.now()}`,
    code: couponData.code,
    type: couponData.type || "percent",
    value: couponData.value || 0,
    minOrder: couponData.minOrder || 0,
    maxUses: couponData.maxUses || 0,
    description: couponData.description || "",
    active: couponData.active !== false,
    created_at: couponData.created_at || new Date().toISOString(),
  };

  const exists = local.findIndex((c) => c.id === coupon.id);
  let updated;
  if (exists >= 0) {
    updated = local.map((c) => (c.id === coupon.id ? coupon : c));
  } else {
    updated = [coupon, ...local];
  }
  saveCouponsLocal(updated);

  if (isSupabaseConfigured && supabase) {
    try {
      const { error } = await supabase.from("coupons").upsert({
        id: coupon.id,
        code: coupon.code,
        type: coupon.type,
        value: coupon.value,
        min_order: coupon.minOrder,
        max_uses: coupon.maxUses,
        description: coupon.description,
        active: coupon.active,
        created_at: coupon.created_at,
      });
      if (error) console.error("Failed to save coupon:", error);
    } catch (err) {
      console.error("Failed to save coupon:", err);
    }
  }

  return updated;
};

export const deleteCoupon = async (couponId) => {
  const local = getCoupons();
  const updated = local.filter((c) => c.id !== couponId);
  saveCouponsLocal(updated);

  if (isSupabaseConfigured && supabase) {
    try {
      await supabase.from("coupons").delete().eq("id", couponId);
    } catch (err) {
      console.error("Failed to delete coupon:", err);
    }
  }

  return updated;
};
